import { RefObject, useCallback, useEffect, useState } from 'react';
import {
  Account,
  ResponseError,
  useQueryState,
  Response,
  usePrevious,
  useGetList,
  SelectItemProps,
  useFilters,
  useScroll,
  PermissionEnum,
  SchemaFilterType,
} from '@ui/src';
import { useAppDispatch } from '@store';
import { filtersSchema, schema } from '../variables/list';
import {
  useGetStatementsAccountsCategoriesQuery,
  useLazyGetStatementItemsFileQuery,
  useLazyGetStatementPdfQuery,
  useLazyGetStatementsListQuery,
} from '../../store/services/list';
import { useLazyGetAccountsListQuery } from '../../../accounts/store/services';
import { useLazyGetPspAccountsQuery } from '../../../../store/dictionaries/services';
import { StatementListItem } from '../types';
import getPeriodDateString from '../helpers/get-period-date-string';
import { clearStatementsListState } from '../../store/slices/list';

const useGetListFunctions = ({
  currencies,
  statements,
  activePermissions,
  search,
  scrollRef,
}: {
  currencies?: SelectItemProps[];
  statements?: StatementListItem[];
  activePermissions?: PermissionEnum[];
  search?: string;
  scrollRef: RefObject<HTMLDivElement | null>;
}) => {
  const dispatch = useAppDispatch();

  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [downloadedId, setDownloadedId] = useState('');
  const [statementError, setStatementError] = useState('');
  const [statementPanelIsVisible, setStatementPanelIsVisible] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [selectedDateType, setSelectedDateType] = useState('today');
  const [statementFileError, setStatementFileError] = useState('');

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const { isLoading: accountsCategoriesLoading } =
    useGetStatementsAccountsCategoriesQuery('all=true&include=primaryAccounts');

  const prevFilters = usePrevious(filters);

  const [
    getAccountsList,
    {
      isLoading: accountsListLoading,
      isFetching: accountsListUpdateInProgress,
    },
  ] = useLazyGetAccountsListQuery();

  const [getPspAccounts] = useLazyGetPspAccountsQuery();
  const [
    getStatementPdf,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetStatementPdfQuery();

  const [
    getStatementFile,
    {
      isLoading: fileIsLoading,
      isFetching: fileIsFetching,
      isSuccess: fileIsSuccess,
    },
  ] = useLazyGetStatementItemsFileQuery();

  const [getStatementsList, { isLoading, isFetching }] =
    useLazyGetStatementsListQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<StatementListItem>({
      getList: getStatementsList,
      onSortChange,
      onFilterChange,
      schema,
    });

  const getAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc`,
      );

      if (response && response.data) {
        setAccounts(
          response.data.data.edges.map((item) => item.node) as Account[],
        );
      }
    }
  }, []);

  const onDownloadStatement = async (
    id: string,
    accountId: string,
    organizationId: string,
  ) => {
    setDownloadedId(id);
    setStatementError('');

    const baseQuery = `accountId=${accountId}&organizationId=${organizationId}`;

    const response = (await getStatementPdf({ id, query: baseQuery })) as {
      error: ResponseError;
      data: any;
    };

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `Statement ${selectedAccount} ${id}.pdf`;
      hiddenElement.click();
      setDownloadedId('');
    } else if (response.error) {
      setDownloadedId('');

      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setStatementError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setStatementError(response.error.data.message);
        } else {
          setStatementError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onSearch = async (searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      const response = (await getAccountsList(
        `?search=${searchText}&all=true&sortProp=fullNumber&sortDir=asc`,
      )) as { data: Response<Account> };

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push(item.node);
        });
      }
      setAccounts(results);
    }
  };

  const onStatementsDownload = async () => {
    const currentAccountId = accounts?.find(
      (item) => item.fullNumber === selectedAccount,
    )?.id;

    const { dateString, dateFromValue, dateToValue } = getPeriodDateString(
      selectedDateType,
      selectedDateRange,
    );

    const datePeriod = `from ${new Date(
      dateFromValue as Date,
    ).toLocaleDateString()} to ${new Date(
      dateToValue as Date,
    ).toLocaleDateString()}`;

    const baseQuery = `accountId=${currentAccountId}&currencyId=${selectedCurrency}&${dateString}`;

    const response = (await getStatementFile({
      query: baseQuery,
      type: selectedFormat,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `Statements ${
        currencies?.find((item) => item.value === selectedCurrency)?.name
      } #${selectedAccount} ${datePeriod}.${selectedFormat}`;
      hiddenElement.click();
    } else if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setStatementFileError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setStatementFileError(response.error.data.message);
        } else {
          setStatementFileError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onCloseStatementPanel = () => {
    setSelectedCurrency('');
    setSelectedDateType('today');
    setSelectedDateRange([]);
    setSelectedFormat('csv');
    setStatementPanelIsVisible(false);
    setStatementFileError('');
  };

  const { filtersComponent, setValues, handleSubmit, reset } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (fileIsSuccess) {
      onCloseStatementPanel();
    }
  }, [fileIsSuccess]);

  useEffect(() => {
    if (filters?.account?.value?.length) {
      reset(filters);
      setSelectedAccount(filters.account.value[0]);
    }
  }, [filters, reset]);

  useEffect(() => {
    if (
      prevFilters?.account?.value?.length &&
      !filters?.account?.value?.length &&
      !selectedAccount &&
      statements
    ) {
      dispatch(clearStatementsListState());
    }
  }, [prevFilters, filters, statements, selectedAccount]);

  useEffect(() => {
    setValues('currencyId', currencies as SelectItemProps[]);
  }, [currencies]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.PSP_LIST)) {
      getPspAccounts();
    }
  }, [activePermissions]);

  useEffect(() => {
    if (
      selectedAccount &&
      search &&
      filters &&
      filters.account?.value?.length &&
      prevFilters &&
      Object.keys(prevFilters).length &&
      !statements &&
      activePermissions?.includes(PermissionEnum.ACCOUNTS_STATEMENTS)
    ) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['30'],
            type: SchemaFilterType.STRING,
          },
        },
        sort,
      });
    }
  }, [
    selectedAccount,
    filters,
    sort,
    search,
    prevFilters,
    activePermissions,
    statements,
  ]);

  useEffect(
    () => () => {
      dispatch(clearStatementsListState());
    },
    [],
  );

  return {
    handleSubmit,
    downloadedId,
    statementError,
    statementPanelIsVisible,
    statementFileError,
    accountsCategoriesLoading,
    accountsListLoading,
    accountsListUpdateInProgress,
    downloadInProgress,
    downloadIsFetching,
    fileIsLoading,
    fileIsFetching,
    isLoading,
    onSort,
    setPage,
    errorList,
    getAccountsListAction,
    onDownloadStatement,
    onSearch,
    onStatementsDownload,
    filtersComponent,
    filters,
    selectedAccount,
    accounts,
    setAccounts,
    setSelectedAccount,
    sort,
    reset,
    onFilterChange,
    getListAction,
    setStatementPanelIsVisible,
    setSelectedCurrency,
    onFilter,
    isFetching,
    onCloseStatementPanel,
    selectedCurrency,
    selectedDateType,
    selectedFormat,
    selectedDateRange,
    setSelectedDateType,
    setSelectedDateRange,
    setSelectedFormat,
  };
};

export default useGetListFunctions;
